import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { EditPage, FuseInput, FuseSelect } from '/src/components';
import { useCompanyStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Company } from '/src/lib/models';
import { DataModelType, FormErrors, Region, SocketLineOfBusiness } from '/src/lib/types';

/**
 * Edit Company Page
 */
export const EditCompanyPage = observer(() => {
  /** Context **/
  const companyStore = useCompanyStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<Company>();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    false, // Notes
    false, // Documents
  ]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    const errorMessages = {
      name: 'Company Name is required',
      socketLineOfBusiness: 'Line of Business is required',
      companyType: 'Company Type is required',
      'address.street': 'Street is required',
      'address.city': 'City is required',
      'address.zip': 'Zip code is required',
      'phone.primary': 'Primary phone number is required',
      contactPerson: 'Contact person is required',
    };

    if (!formValues?.['name']) errors['name'] = errorMessages['name'];
    if (!formValues?.['socketLineOfBusiness']) errors['socketLineOfBusiness'] = errorMessages['socketLineOfBusiness'];
    if (!formValues?.['companyType']) errors['companyType'] = errorMessages['companyType'];
    if (!formValues?.['address']?.street) errors['address.street'] = errorMessages['address.street'];
    if (!formValues?.['address']?.city) errors['address.city'] = errorMessages['address.city'];
    if (!formValues?.['address']?.zip) errors['address.zip'] = errorMessages['address.zip'];
    if (!formValues?.['phone']?.primary) errors['phone.primary'] = errorMessages['phone.primary'];
    if (!formValues?.['contactPerson']) errors['contactPerson'] = errorMessages['contactPerson'];

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the Company.
   *
   * @remarks
   * Redirects to the Company's Detail page afterwards.
   */
  const update = async () => {
    if (!formValues) return;

    // Validate form before submitting
    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      const response = await companyStore.update(formValues);
      if (!response) throw new Error(`Could not update Company with ID "${formValues._id}"`);

      toast.success(toastMessages[response.isLender ? 'updateLender' : 'updateCompany'].success);
      navigate(`/${response.isLender ? 'lenders' : 'companies'}/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages[formValues.isLender ? 'updateLender' : 'updateCompany'].error);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (!params.companyId) return;

    Promise.all([companyStore.get(params.companyId)]);
  }, [params.companyId]);

  useEffect(() => {
    if (companyStore.currentCompany) {
      setFormValues(companyStore.currentCompany);
    }
  }, [companyStore.currentCompany]);

  /** Render **/
  return (
    <EditPage
      modelName={companyStore.currentCompany?.name ?? ''}
      modelType={DataModelType.Company}
      onEdit={update}
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      sidebarFlex="30"
      contentFlex="70"
      noteOptions={{
        notes: formValues?.notes ?? [],
        onChange: updateFormValue('notes'),
      }}
      documentOptions={{
        files: formValues?.files ?? [],
        onChange: updateFormValue('files'),
      }}
    >
      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Company Short Name */}
        <FuseInput
          label="Company Short Name"
          value={formValues?.name}
          updateValue={updateFormValue('name')}
          maxLength={40}
          required
          error={formErrors.name}
        />

        {/* Company Long Name */}
        <FuseInput
          label="Company Long Name"
          value={formValues?.longName}
          updateValue={updateFormValue('longName')}
          maxLength={40}
          error={formErrors.longName}
        />
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Socket Line of Business */}
        <FuseSelect
          label="Line of Business"
          options={Object.values(SocketLineOfBusiness)}
          value={formValues?.socketLineOfBusiness}
          updateValue={updateFormValue('socketLineOfBusiness')}
          placeholder="Select a Line of Business for this Company"
          required // TODO: is this required or not?
          error={formErrors.socketLineOfBusiness}
        />

        {/* Company Type */}
        <FuseInput
          label="Company Type"
          value={formValues?.companyType}
          updateValue={updateFormValue('companyType')}
          maxLength={40}
          required
          error={formErrors.companyType}
        />
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Street */}
        <FuseInput
          label="Street"
          value={formValues?.address.street}
          updateValue={(street) => updateFormValue('address')({ ...formValues?.address, street })}
          maxLength={40}
          required
          error={formErrors['address.street']}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* City */}
        <Box display="flex" flex={60}>
          <FuseInput
            label="City"
            value={formValues?.address.city}
            updateValue={(city) => updateFormValue('address')({ ...formValues?.address, city })}
            maxLength={40}
            required
            error={formErrors['address.city']}
          />
        </Box>

        {/* State */}
        <Box display="flex" flex={15}>
          <FuseSelect
            label="State"
            options={Object.values(Region) as string[]}
            value={formValues?.address.state}
            updateValue={(state) => updateFormValue('address')({ ...formValues?.address, state })}
            placeholder="State"
            flex="15"
            required
            error={formErrors['address.state']}
          />
        </Box>

        {/* Zip */}
        <Box display="flex" flex={25}>
          <FuseInput
            label="Zip"
            value={formValues?.address.zip}
            updateValue={(zip) => updateFormValue('address')({ ...formValues?.address, zip })}
            maxLength={5}
            type="number"
            required
            error={formErrors['address.zip']}
          />
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* PRIMARY PHONE */}
        <FuseInput
          label="Primary Phone"
          value={formValues?.phone?.primary}
          updateValue={(primary) => updateFormValue('phone')({ ...formValues?.phone, primary })}
          required
          error={formErrors['phone.primary']}
        />

        {/* SECONDARY PHONE */}
        <FuseInput
          label="Secondary Phone"
          value={formValues?.phone?.secondary}
          updateValue={(secondary) => updateFormValue('phone')({ ...formValues?.phone, secondary })}
          error={formErrors['phone.secondary']}
        />

        {/* FAX */}
        <FuseInput
          label="Fax"
          value={formValues?.phone?.fax}
          updateValue={(fax) => updateFormValue('phone')({ ...formValues?.phone, fax })}
          error={formErrors['phone.fax']}
        />
      </Box>

      {/* CONTACT PERSON */}
      <FuseInput
        label="Contact Person"
        value={formValues?.contactPerson}
        updateValue={updateFormValue('contactPerson')}
        maxLength={40}
        required
        error={formErrors.contactPerson}
      />

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* EMAIL */}
        <FuseInput
          label="Email"
          value={formValues?.email}
          updateValue={updateFormValue('email')}
          type="email"
          maxLength={40}
          error={formErrors.email}
        />

        {/* WEBSITE */}
        <FuseInput
          label="Website"
          value={formValues?.website}
          updateValue={updateFormValue('website')}
          type="url"
          maxLength={40}
          error={formErrors.website}
        />
      </Box>
    </EditPage>
  );
});
